import React, {
  FC,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactSelect, {
  type ClearIndicatorProps,
  components,
  type ControlProps,
  type CSSObjectWithLabel,
  type DropdownIndicatorProps,
  type FormatOptionLabelMeta,
  type GroupBase,
  type IndicatorsContainerProps,
  type InputProps,
  type MenuListProps,
  type MenuPlacement,
  type MultiValue,
  type MultiValueProps,
  type MultiValueRemoveProps,
  type Options,
  type SingleValue,
  type SingleValueProps,
  type ValueContainerProps
} from 'react-select';
import ReactAsyncSelect from 'react-select/async';
import ReactAsyncCreatableSelect from 'react-select/async-creatable';
import Select from 'react-select/base';
import ReactCreatableSelect from 'react-select/creatable';
import { Box, Stack, type SxProps, Typography, useTheme } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { mergeSx as mergeSxOriginal } from 'merge-sx';
import { MenuPortalProps } from 'node_modules/react-select/dist/declarations/src/components/Menu';

import CheckboxChecked from '@/assets/icons/checkbox-checked.svg?react';
import CheckboxEmpty from '@/assets/icons/checkbox-empty.svg?react';
import ChevronDown from '@/assets/icons/chevron-down.svg?react';
import withFormControl from '@/hoc/withFormControl';
import { borders, transforms, transitions } from '@/theme/constants';
import shadows from '@/theme/shadows';
import typography from '@/theme/typography';

import ClearIcon from './assets/clear.svg?react';

const mergeSx = mergeSxOriginal as <T extends object>(...styles: (T | false | undefined)[]) => T;

interface PortalProps {
  menuPlacement?: MenuPlacement;
  menuPortalTarget?: HTMLElement;
}

export interface DropdownSelectOption {
  value: string;
  label: string;
  labelElement?: ReactNode;
  Icon?: ReactElement;
  __isNew__?: boolean;
}

export interface DropdownSelectProps {
  id?: string;
  placeholder?: string;
  name: string;
  label?: string;
  value: string[] | string;
  onChange?: (newVal: string[] | string) => void;
  options?: DropdownSelectOption[];
  loadOptions?: (inputValue: string) => Promise<DropdownSelectOption[]> | undefined;
  loadSelectedOptions?: (value: string[]) => Promise<DropdownSelectOption[]>;
  isMulti?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  hideCheckboxes?: boolean;
  sx?: SxProps<Theme>;
  error?: string;
  usePortal?: boolean;
  isDisabled?: boolean;
  menuPlacement?: MenuPlacement;
  className?: string;
  fixedMenuPosition?: boolean;
  useCreatable?: boolean;
  onCreateOption?: (newOption: string) => void;
  formatCreateOption?: (inputValue: string) => ReactNode;
  overrideStyles?: {
    [key: string]: CSSObjectWithLabel;
  };
  isValidNewOption?: (inputValue: string) => boolean;
  disabledOptions?: string[];
  maxOptions?: number;
}

const {
  DropdownIndicator: OriginalDropdownIndicator,
  MultiValueRemove: OriginalMultiValueRemove,
  Control: OriginalControl,
  IndicatorsContainer: OriginalIndicatorsContainer,
  SingleValue: OriginalSingleValue,
  MenuList: OriginalMenuList,
  Input: OriginalInput,
  ClearIndicator: OriginalClearIndicator,
  ValueContainer: OriginalValueContainer,
  MultiValue: OriginalMultiValue,
  MenuPortal: OriginalMenuPortal
} = components;

const Input = (props: InputProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>) => (
  <OriginalInput className="DropdownSelect-input" {...props} />
);

const DropdownIndicator = (
  props: DropdownIndicatorProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>
) => (
  <OriginalDropdownIndicator {...props} className="DropdownSelect-indicator">
    <ChevronDown className="DropdownSelect-expandMoreIcon" />
  </OriginalDropdownIndicator>
);

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  // When disabled, render an invisible spacer to maintain right-side padding.
  // flexShrink: 0 ensures the spacer maintains its width even when the container is constrained.
  if (props.selectProps.isDisabled) {
    return (
      <Box
        sx={{
          width: theme => theme.spacing(2),
          height: theme => theme.spacing(2),
          flexShrink: 0
        }}
        aria-hidden="true"
      />
    );
  }

  return (
    <OriginalMultiValueRemove {...props}>
      <ClearIcon className="DropdownSelect-clearIcon" />
    </OriginalMultiValueRemove>
  );
};

const ClearIndicator = (props: ClearIndicatorProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>) => (
  <OriginalClearIndicator {...props}>
    <ClearIcon className="DropdownSelect-clearIcon" />
  </OriginalClearIndicator>
);

const SingleValueComponent = (
  props: SingleValueProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>
) => <OriginalSingleValue {...props} className="DropdownSelect-singleValue" />;

const Control = (props: ControlProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>) => (
  <OriginalControl {...props} className="DropdownSelect-control" />
);

const IndicatorsContainer = (
  props: IndicatorsContainerProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>
) => <OriginalIndicatorsContainer {...props} className="DropdownSelect-indicatorsContainer" />;

const MenuList = (props: MenuListProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>) => (
  <OriginalMenuList {...props} className="DropdownSelect-menuList" />
);

const ValueContainer = (props: ValueContainerProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>) => (
  <OriginalValueContainer {...props} className="DropdownSelect-valueContainer" />
);

const MultiValue = (props: MultiValueProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>) => (
  <OriginalMultiValue {...props} className="DropdownSelect-multiValue" />
);

const MenuPortal = (props: MenuPortalProps<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>) => (
  <OriginalMenuPortal {...props} className="DropdownSelect-menuPortal" />
);

type SelectComponent = Select<DropdownSelectOption, boolean, GroupBase<DropdownSelectOption>>;

const defaultFormatCreateOption = (inputValue: string) => `Create "${inputValue}"`;

const DropdownSelectField: FC<DropdownSelectProps> = forwardRef(
  (
    {
      id,
      placeholder: propPlaceholder,
      name,
      value,
      label,
      onChange,
      options,
      loadOptions,
      loadSelectedOptions,
      isMulti = true,
      isSearchable = true,
      isClearable = false,
      hideCheckboxes = false,
      sx,
      error,
      usePortal,
      isDisabled,
      menuPlacement,
      className,
      fixedMenuPosition = false,
      useCreatable,
      onCreateOption,
      formatCreateOption = defaultFormatCreateOption,
      overrideStyles,
      isValidNewOption,
      disabledOptions,
      maxOptions
    }: DropdownSelectProps,
    ref
  ) => {
    const [optionsCache, setOptionsCache] = useState<{ [key: string]: DropdownSelectOption | null }>({});
    const { palette, spacing } = useTheme();
    const { t } = useTranslation('common');
    const dropdownRef = useRef<SelectComponent | null>(null);
    const placeholder = propPlaceholder || t('components.select.placeholder');

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          dropdownRef.current?.focusInput();
          dropdownRef.current?.openMenu('first');
        }
      }),
      []
    );
    const handleChange = useCallback(
      (selectedOptions: MultiValue<DropdownSelectOption> | SingleValue<DropdownSelectOption>) => {
        if (isMulti) {
          const selectedMultiOptions = selectedOptions as MultiValue<DropdownSelectOption>;
          const newValues = (selectedMultiOptions || []).map(option => option.value);
          if (typeof maxOptions === 'number' && newValues.length > maxOptions) {
            return;
          }
          onChange?.(newValues);
        } else {
          const selectedSingleOption = selectedOptions as SingleValue<DropdownSelectOption>;
          onChange?.(selectedSingleOption?.value as string);
        }
      },
      [isMulti, onChange, maxOptions]
    );

    const isOptionSelected = useCallback(
      (option: DropdownSelectOption) => {
        if (!isMulti || !Array.isArray(value)) return value === option.value;

        return (value as string[])?.includes(option.value) || false;
      },
      [isMulti, value]
    );

    const isOptionDisabled = useCallback(
      (option: DropdownSelectOption, selectValue: Options<DropdownSelectOption>) => {
        const selectValueArray = Array.isArray(selectValue) ? selectValue : [selectValue];

        if (selectValueArray.find(selectItem => selectItem.value === option.value)) {
          return false;
        }

        if (isMulti && typeof maxOptions === 'number' && selectValueArray.length >= maxOptions) {
          return true;
        }

        return disabledOptions?.includes(option.value) ?? false;
      },
      [disabledOptions, isMulti, maxOptions]
    );

    const formatOptionLabel = useCallback(
      (option: DropdownSelectOption, meta: FormatOptionLabelMeta<DropdownSelectOption>) => {
        if (option.__isNew__) {
          return formatCreateOption(option.label);
        }

        const isSelected = isOptionSelected(option);
        const labelText = option.label ?? '';

        const matches = match(labelText, meta.inputValue, { insideWords: true, findAllOccurrences: true });
        const parts = parse(labelText, matches);
        const textLabel = parts?.map((part, index) => (
          <Typography
            key={index}
            className="DropdownSelect-optionTextPart"
            sx={{
              ...(meta.context === 'menu' ? typography.body2 : typography.body3),
              color: 'grey.900',
              fontWeight: part.highlight ? typography.fontWeightMedium : typography.fontWeightRegular,
              whiteSpace: 'pre'
            }}
          >
            {part.text || <span>&nbsp;</span>}
          </Typography>
        ));

        const isDisabled = isOptionDisabled(option, meta.selectValue);

        return (
          <Stack
            direction="row"
            alignItems="center"
            className="DropdownSelect-optionLabel"
            data-context={meta?.context}
            gap={1}
            sx={{
              // TODO: Find a better solution and align with designers
              opacity: isDisabled ? 0.5 : 1
            }}
          >
            {isMulti && !hideCheckboxes && (
              <Box className="CheckboxIcon-root" sx={{ minWidth: spacing(4) }}>
                {isSelected ? <CheckboxChecked /> : <CheckboxEmpty />}
              </Box>
            )}
            <Stack direction="row" alignItems="center" gap={1}>
              {option.Icon && <Box className="DropdownSelect-optionIcon">{option.Icon}</Box>}
              <Stack direction="row" alignItems="center" className="DropdownSelect-optionText">
                {meta?.context === 'menu' && (option.labelElement || textLabel)}
                {meta?.context === 'value' && textLabel}
              </Stack>
            </Stack>
          </Stack>
        );
      },
      [formatCreateOption, hideCheckboxes, isMulti, isOptionDisabled, isOptionSelected, spacing]
    );

    const formatCreateLabel = useCallback((inputValue: string) => inputValue, []);

    const loadAndCacheOptions = useCallback(
      async (ids: string[]) => {
        if (!loadSelectedOptions) return;
        try {
          const loadedOptions = await loadSelectedOptions(ids);
          if (!loadedOptions.length) return;
          setOptionsCache(currentCache => ({
            ...currentCache,
            ...Object.fromEntries(loadedOptions.map(option => [option.value, option || null]))
          }));
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
          setOptionsCache(currentCache => ({
            ...currentCache,
            ...Object.fromEntries(ids.map(id => [id, null]))
          }));
        }
      },
      [loadSelectedOptions]
    );

    const loadOptionsProxy = useCallback(
      async (inputValue: string) => {
        if (!loadOptions) return [];

        const loadedOptions = await loadOptions(inputValue);

        if (!loadedOptions) return [];

        const missingOptions = loadedOptions.some(option => optionsCache[option.value] === undefined);

        if (missingOptions) {
          setOptionsCache(currentCache => ({
            ...currentCache,
            ...Object.fromEntries(loadedOptions.map(option => [option.value, option || null]))
          }));
        }

        return loadedOptions;
      },
      [loadOptions, optionsCache]
    );

    const preparedMultiValue = useMemo(() => {
      const multiValue: string[] = Array.isArray(value) ? value : [value].filter(Boolean);

      if (!isMulti || !multiValue) return [];

      if (!loadOptions) return (options || []).filter(option => multiValue?.includes(option.value));

      const missingOptions = multiValue.filter(id => optionsCache[id] === undefined);
      missingOptions.length && loadAndCacheOptions(missingOptions);

      return multiValue
        .map(id => optionsCache[id])
        .filter(Boolean)
        .map(option => option!);
    }, [isMulti, loadAndCacheOptions, loadOptions, options, optionsCache, value]);

    const preparedSingleValue = useMemo(() => {
      const singleValue = value as string;

      if (isMulti || !singleValue) return null;

      if (!loadOptions) return (options || []).find(option => singleValue === option.value);

      const missingOptions = optionsCache[singleValue] ? [] : [singleValue];
      missingOptions.length && loadAndCacheOptions(missingOptions); // Intentionally without await. It's a side-effect

      return optionsCache[singleValue];
    }, [isMulti, loadAndCacheOptions, loadOptions, options, optionsCache, value]);

    const preparedValue = useMemo(
      () => (isMulti ? preparedMultiValue : preparedSingleValue),
      [isMulti, preparedMultiValue, preparedSingleValue]
    );

    const hasValue = useMemo(
      () => !!(Array.isArray(preparedValue) ? preparedValue?.length : preparedValue),
      [preparedValue]
    );

    const customStyles = useMemo(
      () => ({
        control: (base: CSSObjectWithLabel, state: { isFocused?: boolean }) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              backgroundColor: palette.background.default,
              padding: 0,
              border: borders.getSolid(palette.grey[hasValue ? 600 : 400]!, borders._0_5px),
              boxShadow: hasValue && state.isFocused ? shadows[2] : 'none',

              '&:hover': {
                borderColor: palette.grey[900]
              },
              '& .CheckboxIcon-root': {
                display: 'none'
              },
              '& .DropdownSelect-valueContainer': {
                padding: spacing(0.75),
                gap: spacing(0.5),
                position: 'static'
              },
              '& .DropdownSelect-valueContainer input[role=combobox][aria-readonly=true]': {
                position: 'absolute'
              },
              '& .DropdownSelect-clearIcon': {
                cursor: 'pointer'
              },
              ...(error && {
                borderWidth: borders._1px,
                borderColor: palette.error.main,
                '&:hover': {
                  borderColor: palette.error.main
                }
              })
            },
            isDisabled && {
              backgroundColor: palette.grey[200]
            },
            isMulti && {
              maxHeight: spacing(10),
              overflowY: 'auto'
            },
            overrideStyles?.control ?? {}
          ),
        option: (base: CSSObjectWithLabel, { isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              padding: spacing(0.75, 1),
              backgroundColor: isFocused || isSelected ? palette.primary[100] : palette.background.default,
              color: palette.text.primary,
              '& .MuiAvatar-root': {
                margin: 0
              },
              '&:hover': {
                backgroundColor: palette.primary[100]
              }
            },
            overrideStyles?.option ?? {}
          ),
        multiValue: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              backgroundColor: palette.background.default,
              border: borders.half,
              borderColor: palette.grey[700],
              borderRadius: spacing(0.5),
              margin: 0,
              '& .DropdownSelect-multiValue:not([role=button])': {
                padding: spacing(0.5, 1),
                paddingInlineEnd: 0.5
              }
            },
            overrideStyles?.multiValue ?? {}
          ),
        multiValueLabel: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              color: palette.grey[900]
            },
            overrideStyles?.multiValueLabel ?? {}
          ),
        multiValueRemove: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              color: palette.grey[700],
              '&:hover': {
                backgroundColor: palette.grey[300]
              }
            },
            overrideStyles?.multiValueRemove ?? {}
          ),
        placeholder: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              ...(typography.body2 as CSSObjectWithLabel),
              textAlign: 'start',
              color: palette.grey[700]
            },
            overrideStyles?.placeholder ?? {}
          ),
        menu: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              '& .CheckboxIcon-root': {
                display: 'flex'
              }
            },
            overrideStyles?.menu ?? {}
          ),
        dropdownIndicator: (base: CSSObjectWithLabel, state: { selectProps: { menuIsOpen: boolean } }) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              transform: transforms.mirrorY(state.selectProps.menuIsOpen),
              transition: transitions.transform
            },
            overrideStyles?.dropdownIndicator ?? {}
          ),
        indicatorsContainer: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(base, overrideStyles?.indicatorsContainer ?? {}),
        menuPortal: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            { zIndex: 10000, position: fixedMenuPosition ? 'fixed' : 'absolute' },
            overrideStyles?.menuPortal ?? {}
          ),
        input: (base: CSSObjectWithLabel) =>
          mergeSx<CSSObjectWithLabel>(
            base,
            {
              fontSize: typography?.body2?.fontSize
            },
            overrideStyles?.input ?? {}
          )
      }),
      [
        palette.background.default,
        palette.grey,
        palette.error.main,
        palette.primary,
        palette.text.primary,
        hasValue,
        spacing,
        error,
        isDisabled,
        isMulti,
        overrideStyles,
        fixedMenuPosition
      ]
    );

    const SelectComponent = useMemo(() => {
      switch (true) {
        case !!loadOptions && !useCreatable:
          return ReactAsyncSelect;
        case !!useCreatable && !loadOptions:
          return ReactCreatableSelect;
        case !!useCreatable && !!loadOptions:
          return ReactAsyncCreatableSelect;
        default:
          return ReactSelect;
      }
    }, [loadOptions, useCreatable]);

    const portalProps = useMemo(
      () =>
        ({
          ...(usePortal && {
            menuPortalTarget: document.body,
            menuPlacement: 'auto'
          })
        }) as PortalProps,
      [usePortal]
    );

    const dynamicProps = useMemo(() => {
      switch (true) {
        case !!loadOptions && !onCreateOption:
          return {
            loadOptions: loadOptionsProxy,
            defaultOptions: true
          };
        case !!useCreatable && !loadOptions:
          return {
            onCreateOption,
            formatCreateLabel,
            isValidNewOption
          };

        case !!loadOptions && !!onCreateOption:
          return {
            loadOptions: loadOptionsProxy,
            defaultOptions: true,
            onCreateOption,
            formatCreateLabel,
            isValidNewOption
          };
        default:
          return {
            options: isClearable ? ([{ value: '', label: '' }, ...(options || [])] as DropdownSelectOption[]) : options
          };
      }
    }, [
      loadOptions,
      onCreateOption,
      loadOptionsProxy,
      useCreatable,
      formatCreateLabel,
      isValidNewOption,
      isClearable,
      options
    ]);

    return (
      <Box sx={sx} className={className}>
        <SelectComponent
          ref={dropdownRef}
          name={name}
          components={{
            DropdownIndicator,
            MultiValueRemove,
            IndicatorSeparator: () => null,
            ClearIndicator,
            Control,
            IndicatorsContainer,
            SingleValue: SingleValueComponent,
            MenuList,
            Input,
            ValueContainer,
            MultiValue,
            MenuPortal
          }}
          value={preparedValue}
          onChange={handleChange}
          styles={customStyles}
          placeholder={placeholder ?? label}
          formatOptionLabel={formatOptionLabel}
          isSearchable={isSearchable}
          closeMenuOnSelect={!isMulti}
          hideSelectedOptions={false}
          controlShouldRenderValue
          isMulti={isMulti}
          inputId={id}
          isClearable={isClearable}
          isDisabled={isDisabled}
          menuPlacement={menuPlacement}
          isOptionDisabled={isOptionDisabled}
          {...dynamicProps}
          {...portalProps}
        />
      </Box>
    );
  }
);

DropdownSelectField.displayName = 'DropdownSelectField';

const DropdownSelect = withFormControl<string[] | string, string[] | string, DropdownSelectProps>(DropdownSelectField);

export default DropdownSelect;
